import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import {
  concatMap,
  merge,
  Observable,
  startWith,
  Subscription,
  take,
  tap,
  throttleTime,
  combineLatest,
  filter,
  switchMap,
  of, debounceTime,
} from 'rxjs';

import { Resource } from '@shared/resource/models';
import {
  CanvasComponent,
  CanvasObjectMultiSelected,
  FLOORPLAN_GLOBAL_CLASSNAME,
} from '@tablesready/canvas';
import { GroupedItems } from '@shared/floor-plan/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import { PartySource, PartyTypes } from '@shared/party/enums';
import { NotificationTime } from '@shared/restaurant/enums';
import { Party } from '@shared/party/models';
import { PartyBadge } from '@tablesready/frontend-badge';
import {
  DateTimeService,
  FloorplanResources,
  PageIdentifierService, SearchBarComponent,
  SharedTabItem,
} from '@tablesready/common-ui';
import {
  FloorPlanParty,
  FloorPlanPartyType,
  PartyActionEvent,
  FloorPlanPartyAction,
  FloorPlanPartyTab,
} from '@tablesready/frontend-timeline';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { HeaderTitleService } from '@tablesready/frontend-header';
import { AuthRestaurantService, SocketService } from '@tablesready/frontend-store';
import { ClockService } from '@tablesready/frontend-alerts-bar';
import { AppTheme, ThemeSwitchService } from '@tablesready/frontend-theme-switch';
import { LocalStorageService } from '@tablesready/frontend-storage';
import { FrontendBreakpointsService } from '@tablesready/frontend-breakpoints';
import { WindowHeightService } from '@tablesready/frontend-nav-sidebar';
import { FLOOR_PLAN_VIEW_TABLET_BREAKPOINT } from './floorplan-view.interfaces';
import {
  FloorPlanTableService,
  MessageButtonService,
  PartiesService,
  ResourcesService,
} from '../../../services';
import { PlansAlertData } from '../../../models';

const ONE_MINUTE = 60000;

@Component({
  selector: 'tablesready-floorplan-view',
  templateUrl: './floorplan-view.component.html',
  styleUrls: ['./floorplan-view.component.scss'],
  providers: [
    FloorPlanTableService,
    PageIdentifierService,
    MessageButtonService,
  ],
})
export class FloorplanViewComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @ViewChild('searchBarRef') searchBarRef?: SearchBarComponent;
  @Input() resources: Resource[] = [];
  @Input() reservationPluralLabel = 'reservations';
  @Input() locationTimezone = 'America/New_York';
  @Input() notificationTime = NotificationTime.Relative;
  @Input() voicePageNotificationEnabled = false;
  @Input() smsNextMessage = '';
  @Input() autoRemoveTime?: number;
  @Input() customerPlural?: string;
  @Input() locationCountry = '';
  @Input() reservationDateFilter: NgbDateStruct;
  @Input() isOnBusinessPlan = false;
  @Input() trialCompleted?: boolean;
  @Input() plansAlertData?: PlansAlertData;
  @ViewChild('graphCanvasView', {
    static: true,
    read: CanvasComponent,
  }) graphCanvasRef?: CanvasComponent;

  @Output() partyAddWithResourceId = new EventEmitter<Partial<PartyActionEvent>>();
  @Output() partyChat = new EventEmitter<PartyActionEvent>();
  @Output() partyCheckIn = new EventEmitter<PartyActionEvent>();
  @Output() partyServed = new EventEmitter<PartyActionEvent>();
  @Output() partyCheckInAndServe = new EventEmitter<PartyActionEvent>();
  @Output() partyComplete = new EventEmitter<PartyActionEvent>();
  @Output() partyDelete = new EventEmitter<PartyActionEvent>();
  @Output() partyEdit = new EventEmitter<PartyActionEvent>();
  @Output() search = new EventEmitter<string>();
  @Output() dateChanged = new EventEmitter<NgbDateStruct>();
  @Output() tablesCount = new EventEmitter<number>();
  @Output() floorPlanIdSelected = new EventEmitter<string>();
  @Output() tabChange = new EventEmitter<SharedTabItem>();
  @Output() floorPlansLoaded = new EventEmitter<FloorplanResources[]>();

  tabs: SharedTabItem[] = [];
  tabIndex = 0;
  now = 0;
  autoZoomed = false;
  icon = AppIcon;
  isDateSetForToday = false;
  isTabletLayout = false;
  color = AppIconColor;
  iconColor: AppIconColor;
  iconSize = AppIconSize;
  minDate?: NgbDateStruct;
  tabType = FloorPlanPartyTab;
  partyTypes = PartyTypes;
  searchTerm = '';
  selectedPartyId?: string;
  selectedPartyResourceName?: string;
  selectedFloorPlanId?: string;
  selectedResourceId?: string;
  currentFloorPlanResourceIds: string[] = [];
  showTableInfoCards = false;
  resourceNameMap: Record<string, string> = {};
  showTablesWarning = false;
  showFloorPlansWarning = false;
  isLoading = true;
  leftLayoutOffset = 0;
  currentFloorPlanPartiesOnly = false;
  isMobile = false;
  cardsToDisplay: Record<FloorPlanPartyTab, FloorPlanParty[]> = {
    [FloorPlanPartyTab.serving]: [],
    [FloorPlanPartyTab.waiting]: [],
    [FloorPlanPartyTab.booked]: [],
    [FloorPlanPartyTab.all]: [],
  };

  cardTypeColorMap: Record<FloorPlanPartyType, string> = {
    [FloorPlanPartyType.checkedIn]: '#FFB800',
    [FloorPlanPartyType.booked]: '#80D8F4',
    [FloorPlanPartyType.serving]: '#0EAD69',
    [FloorPlanPartyType.waiting]: '#7685B4',
    [FloorPlanPartyType.overdue]: '#EB6E6E',
  };

  private sidebarOpen = false;
  private parties: FloorPlanParty[] = [];
  private selectedDate: number;
  private readonly subscriptions = new Subscription();

  get displayNoParties(): boolean {
    const tablePartiesEmpty = this.selectedResourceId && this.cardsToDisplay[FloorPlanPartyTab.all].length === 0;
    const tabEmpty = !this.selectedPartyId && !this.selectedResourceId &&
      this.cardsToDisplay[this.tabs[this.tabIndex]?.title]?.length === 0;

    return (!this.isLoading && (tabEmpty || tablePartiesEmpty)) || !this.isOnBusinessPlan;
  }

  get isSidebarOpen(): boolean {
    return this.sidebarOpen;
  }

  constructor(
    readonly service: FloorPlanTableService,
    private readonly cdr: ChangeDetectorRef,
    readonly messageButtonService: MessageButtonService,
    private readonly windowHeightService: WindowHeightService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly breakpointService: FrontendBreakpointsService,
    private readonly pageIdentifyService: PageIdentifierService,
    private readonly storage: LocalStorageService,
    private readonly clockService: ClockService,
    private readonly router: Router,
    private readonly headerTitleService: HeaderTitleService,
    private readonly partiesService: PartiesService,
    private readonly socketService: SocketService,
    private readonly themeService: ThemeSwitchService,
    private readonly dateTimeService: DateTimeService,
    private readonly resourcesService: ResourcesService,
    private readonly authRestaurantService: AuthRestaurantService,
  ) {
    this.pageIdentifyService.setup(FLOORPLAN_GLOBAL_CLASSNAME);
  }

  pageLoad(): void {
    this.graphCanvasRef.windowResize('FloorplanViewComponent autoZoom()');
    this.autoZoomed = false;
    this.graphCanvasRef.onFloorChange(this.selectedFloorPlanId, 'pageLoad()');
  }

  clearSearchBar(): void {
    if (!this.searchBarRef) {
      return;
    }

    this.searchBarRef.clearSearchField();
  }

  floorChange(id: string): void {
    const floorPlanChanged = this.selectedFloorPlanId !== id;
    this.selectedResourceId = undefined;
    this.selectedPartyId = floorPlanChanged ? undefined : this.selectedPartyId;
    this.selectedFloorPlanId = id;
    this.setTableInfoVisibility(this.selectedPartyId !== undefined && !floorPlanChanged);
    this.currentFloorPlanResourceIds = this.service.savedTables.map(table => table.resourceId);
    this.floorPlanIdSelected.emit(id);
  }

  tabChanged(newIndex: number): void {
    this.tabIndex = newIndex;
    this.selectedResourceId = undefined;
    this.selectedPartyId = undefined;
    this.tabChange.emit(this.tabs[newIndex]);
    this.setTableInfoVisibility(false);
  }

  addParty(isWalkInParty: boolean, resourceId: string): void {
    const action: Partial<PartyActionEvent> = { serveNow: isWalkInParty };
    if (isWalkInParty) {
      action.resourceId = resourceId;
      action.skipName = true;
    }

    this.partyAddWithResourceId.emit(action);
  }

  getPartyById(partyId: string): PartyBadge {
    const party = this.parties.find(item => item._id === partyId);
    return {
      type: party.type,
      source: party.source,
      confirmed_time: party.confirmed_time,
    };
  }

  getItems = (floorPlanId: string): Observable<GroupedItems> => this.service.getItems(floorPlanId)
    .pipe(tap((items) => {
      this.tablesCount.emit(items.tables.length);
      this.changeDate();
      if (this.autoZoomed) {
        return;
      }

      setTimeout(() => {
        if (items.tables.length > 0) {
          this.graphCanvasRef.autoZoom(items.tables);
          this.autoZoomed = true;
        }
      });
    }));

  ngOnDestroy(): void {
    this.messageButtonService.ngOnDestroy();
    this.pageIdentifyService.remove(FLOORPLAN_GLOBAL_CLASSNAME);
    this.subscriptions.unsubscribe();
  }

  objectMultiSelected(data: CanvasObjectMultiSelected, searchTerm = ''): void {
    if (!data.selected) {
      this.closePartyOrTableInfo();
      return;
    }

    if (data.isActionSourceUser) {
      this.selectedPartyId = undefined;
    }

    this.selectedResourceId = data.resourceId;
    this.setTableInfoVisibility(data.resourceId !== undefined);
    if (!data.resourceId) {
      return;
    }

    this.setSidebarOpen(true);
    this.searchParties(searchTerm, data.resourceId);
  }

  setSidebarOpen(isOpen: boolean): void {
    this.sidebarOpen = isOpen;
    this.graphCanvasRef.windowResize(`FloorplanViewComponent setSidebarOpen(${isOpen})`);
  }

  closePartyOrTableInfo(): void {
    this.selectedResourceId = undefined;
    this.selectedPartyId = undefined;
    this.setTableInfoVisibility(false);
  }

  setTableInfoVisibility(show: boolean): void {
    this.showTableInfoCards = show && !this.selectedPartyId;
    if (!show) {
      this.graphCanvasRef.deselectAllObjects();
      this.searchParties(this.searchTerm);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.resources) {
      this.resourceNameMap = {};
      this.resources.forEach((resource) => {
        this.resourceNameMap[resource._id] = resource.name;
      });
    }
  }

  cardToParty(card: FloorPlanParty): Party {
    const party = { ...card };

    delete party.cardType;
    delete party.resourceName;
    delete party.actions;
    delete party.tabType;

    return party;
  }

  selectTableByResourceId(resourceIds: string[], partyId: string): void {
    this.selectedPartyId = partyId;
    this.selectedPartyResourceName = resourceIds
      .filter(resourceId => this.checkIfResourceEnabled(resourceId))
      .map(resourceId => this.service.getResourceNameByIdWithFloorPlan(resourceId, this.selectedFloorPlanId))
      .join(', ');

    setTimeout(() => {
      if (resourceIds.length === 1) {
        const [id] = resourceIds;
        this.graphCanvasRef.selectObjectByResourceId(id);

        return;
      }

      this.graphCanvasRef.selectResourcesByIds(resourceIds);
    });
  }

  floorPlanSwitcherChange(floorPlanId: string): void {
    this.graphCanvasRef.floorPlanId = floorPlanId;
    this.graphCanvasRef.onFloorChange(floorPlanId, 'floorplan view switcher changed');
  }

  checkWarnings(floorPlansCount: number, tablesCount: number): void {
    this.showTablesWarning = floorPlansCount > 0 && tablesCount === 0;
    this.showFloorPlansWarning = floorPlansCount === 0;
  }

  ngOnInit(): void {
    if (!this.reservationDateFilter) {
      this.setCurrentDate();
    }

    const locSub = this.service.loadingFloorPlanForNewLocation$
      .subscribe(([floorPlansCount, tablesCount]) => {
        this.checkWarnings(floorPlansCount, tablesCount);
      });
    this.subscriptions.add(locSub);

    this.getFloorPlanPartiesSwitchState(this.service.floorPlans?.length);
    const clockSub = this.clockService.clock$
      .pipe(throttleTime(ONE_MINUTE))
      .subscribe(() => {
        this.changeDate();
      });
    this.subscriptions.add(clockSub);

    const leftOffsetSub = this.breakpointService.mainNavSidebarWidth$
      .subscribe((value) => {
        this.leftLayoutOffset = value;
      });
    this.subscriptions.add(leftOffsetSub);

    const themeSub = this.themeService.themeChange$
      .pipe(
        switchMap(() => of(this.themeService.getSavedTheme())),
        tap(theme => this.iconColor = theme === AppTheme.light ? AppIconColor.primary : AppIconColor.white),
      ).subscribe();
    this.subscriptions.add(themeSub);

    this.checkLayout();
    const layoutSub = this.breakpointObserver.observe([
      FLOOR_PLAN_VIEW_TABLET_BREAKPOINT,
    ]).subscribe(() => {
      this.checkLayout();
    });
    this.subscriptions.add(layoutSub);

    const mobileSub = this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.HandsetLandscape])
      .pipe(tap(({ matches: isMobile }) => this.isMobile = isMobile)).subscribe();
    this.subscriptions.add(mobileSub);
  }

  selectNextDay(): void {
    const momentDate = this.dateTimeService.ngbDateToMoment(this.reservationDateFilter, this.locationTimezone);
    const nextDayMomentDate = momentDate.add(1, 'day');

    this.reservationDateFilter = this.dateTimeService.momentToNgbDate(nextDayMomentDate);
    this.changeDate();
  }

  selectPreviousDay(): void {
    const momentDate = this.dateTimeService.ngbDateToMoment(this.reservationDateFilter, this.locationTimezone);
    const previousDayMomentDate = momentDate.subtract(1, 'day');

    this.reservationDateFilter = this.dateTimeService.momentToNgbDate(previousDayMomentDate);
    this.changeDate();
  }

  setCurrentDate(): void {
    this.reservationDateFilter = this.dateTimeService.momentToNgbDate(moment.tz(this.locationTimezone));
    this.changeDate();
  }

  changeDate(): void {
    if (!this.reservationDateFilter) {
      this.setCurrentDate();
    }

    const timestamp = this.dateTimeService
      .ngbDateToMoment(this.reservationDateFilter, this.locationTimezone)
      .valueOf();

    if (!isNaN(timestamp)) {
      this.dateChanged.emit(this.reservationDateFilter);
      const momentDate = this.dateTimeService.ngbDateToMoment(this.reservationDateFilter, this.locationTimezone);
      this.isDateSetForToday = momentDate.date() === moment().date();
      this.selectedDate = momentDate.valueOf();
      const workTime: number[][] = [[
        momentDate.startOf('day').unix() * 1000,
        momentDate.endOf('day').unix() * 1000,
      ]];

      this.getPartiesByWorkTime(workTime)
        .subscribe();
    }
  }

  colorTablesByClosestParties(unsortedParties: Party[]): void {
    if (!this.graphCanvasRef) {
      return;
    }

    const parties = unsortedParties.sort((a, b) => a.quoted_time - b.quoted_time);
    const closestPartyByResourceId: Record<string, Party> = {};
    this.graphCanvasRef.resetTableColors(this.service.resourcesMap);

    parties.forEach((party) => {
      party.resources.forEach((resource) => {
        if (closestPartyByResourceId[resource] !== undefined) {
          return;
        }

        closestPartyByResourceId[resource] = party;
      });
    });

    Object.entries(closestPartyByResourceId).forEach(([resourceId, party]) => {
      const partyType = this.getPartyType(party);
      const timeToShow = partyType === FloorPlanPartyType.serving ? party.quoted_time + party.duration : party.quoted_time;
      let partyCompletedPercentage = 0;

      if (partyType === FloorPlanPartyType.serving) {
        const timePassedAfterPartyStaredServing = Date.now() >= party.quoted_time ? Date.now() - party.quoted_time : 0;
        partyCompletedPercentage = Math.floor((timePassedAfterPartyStaredServing / party.duration) * 100);

        if (partyCompletedPercentage >= 100) {
          partyCompletedPercentage = 100;
        }
      }

      this.graphCanvasRef.setTableTextPropName(
        'resourceId',
        resourceId,
        false,
        !!party.seated_time,
        partyCompletedPercentage,
        undefined,
        String(party.size),
        moment.unix(timeToShow / 1000).tz(this.locationTimezone)
          .format('LT'),
      );
      this.graphCanvasRef.setTableColor(resourceId, this.cardTypeColorMap[partyType]);
    });
    this.graphCanvasRef.detectChanges();
  }

  currentFloorPlanPartiesSwitchChange(newValue: boolean): void {
    this.currentFloorPlanPartiesOnly = newValue;
    this.changeDate();
    this.saveFloorPlanPartiesSwitchState(this.service.floorPlans?.length);
  }

  saveFloorPlanPartiesSwitchState(floorPlansCount: number): void {
    if (floorPlansCount < 2) {
      return;
    }

    this.storage.set('currentFloorPlanPartiesOnly', this.currentFloorPlanPartiesOnly);
  }

  getFloorPlanPartiesSwitchState(floorPlansCount: number): void {
    let switchState = false;
    if (floorPlansCount > 1) {
      switchState = this.storage.get('currentFloorPlanPartiesOnly');
    }

    this.currentFloorPlanPartiesOnly = switchState;
  }

  updatePartyWithResource([partyId, resourceId]: [string, string]): void {
    const partyToEdit = this.parties.find(party => party._id === partyId);
    if (!partyToEdit) {
      return;
    }

    const party = this.cardToParty(partyToEdit);
    party.quoted_time = moment.tz(this.locationTimezone).seconds(59)
      .valueOf();

    this.partyEdit.emit({
      resourceId,
      cardType: partyToEdit.cardType,
      party,
      showSuccessUpdateAlert: false,
      callback: () => {
        this.partyCheckInAndServe.emit({
          cardType: partyToEdit.cardType,
          party,
          skipCheckServeTracking: true,
          callback: () => {
            this.changeDate();
          },
        });
      },
    });
  }

  searchParties(searchData: string, filterByResourceId?: string): void {
    this.searchTerm = searchData;
    let badgeCount = 0;

    const allTabs = [FloorPlanPartyTab.booked, FloorPlanPartyTab.serving, FloorPlanPartyTab.waiting];
    const cardCounts: Partial<Record<FloorPlanPartyTab, number>> = {
      [FloorPlanPartyTab.serving]: 0,
      [FloorPlanPartyTab.waiting]: 0,
      [FloorPlanPartyTab.booked]: 0,
    };

    this.cardsToDisplay[FloorPlanPartyTab.all] = [];
    const partiesToFilter = this.parties.sort((a, b) => a.quoted_time - b.quoted_time);

    partiesToFilter.forEach((party) => {
      const partyNameLowerCase = party.name.toLowerCase();
      const searchDataLowerCase = searchData.toLowerCase();
      const doesMeetSearchTerm = partyNameLowerCase.includes(searchDataLowerCase) ||
        party.phone.includes(searchDataLowerCase);

      if (!filterByResourceId && !doesMeetSearchTerm) {
        return;
      }

      if (doesMeetSearchTerm) {
        badgeCount += 1;
        cardCounts[party.tabType] += 1;
      }

      if (doesMeetSearchTerm && !filterByResourceId || party.resources.includes(filterByResourceId) && doesMeetSearchTerm) {
        this.cardsToDisplay[FloorPlanPartyTab.all].push(party);
      }
    });

    this.headerTitleService.setDataForRoute('floorplan', { badgeCount });
    this.setTabsData(cardCounts);

    allTabs.forEach((tabName) => {
      this.cardsToDisplay[tabName] = this.cardsToDisplay[FloorPlanPartyTab.all]
        .filter(party => party.tabType === tabName);
    });
  }

  navigateToFloorPlanSetup(): void {
    void this.router.navigate(['settings/preferences/resources/floorplan']);
  }

  ngAfterViewInit(): void {
    const HEIGHT_DEBOUNCE = 200;
    this.windowHeightService.init();

    const heightSub = this.windowHeightService.height$
      .pipe(
        startWith(window.innerHeight),
        debounceTime(HEIGHT_DEBOUNCE),
      )
      .subscribe(() => {
        this.cdr.detectChanges();

        setTimeout(() => {
          this.graphCanvasRef.redrawScrollbars('FloorPlanViewComponent ngAfterViewInit() height sub');
          this.graphCanvasRef.setCanvasDimensions();
        });
      });
    this.subscriptions.add(heightSub);

    const partiesStream = this.socketService.getEventStream(WebSocketEventsEnum.parties);
    const reservationsStream = this.socketService.getEventStream(WebSocketEventsEnum.reservations);
    const textSmsEventStream = this.socketService.getEventStream(WebSocketEventsEnum.TextSmsEvent);
    const voiceSmsEventStream = this.socketService.getEventStream(WebSocketEventsEnum.VoiceSmsEvent);
    const inboundSmsEventStream = this.socketService.getEventStream(WebSocketEventsEnum.InboundSmsEvent);
    const eventsSub = merge(
      partiesStream,
      reservationsStream,
      textSmsEventStream,
      voiceSmsEventStream,
      inboundSmsEventStream,
    )
      .pipe(
        startWith(undefined),
        concatMap(() => {
          let workTime: number[][] = [[
            moment.tz(this.locationTimezone).startOf('day')
              .unix() * 1000,
            moment.tz(this.locationTimezone).endOf('day')
              .unix() * 1000,
          ]];

          const selectedDate = this.dateTimeService.ngbDateToMoment(this.reservationDateFilter, this.locationTimezone);
          const selectedDateStartOfDay = selectedDate.startOf('day').valueOf();
          const selectedDateEndOfDay = selectedDate.endOf('day').valueOf();

          if (workTime[0][0] !== selectedDateStartOfDay) {
            workTime = [[selectedDateStartOfDay, selectedDateEndOfDay]];
          }

          return this.getPartiesByWorkTime(workTime);
        }),
      )
      .subscribe();

    this.subscriptions.add(eventsSub);

    const floorPlansSub = combineLatest([
      this.service.floorPlans$,
      this.service.floorPlanIdToTablesMap$,
    ])
      .pipe(
        filter(([floorPlans]) => floorPlans.length > 0),
        tap(([floorPlans, floorPlanTables]) => {
          this.getFloorPlanPartiesSwitchState(floorPlans.length);

          const floorPlanResources: FloorplanResources[] = floorPlans.map((floorPlan) => {
            const resources = floorPlanTables[floorPlan.id].map(table => table.resourceId);
            return {
              floorPlanName: floorPlan.name,
              resources,
            };
          });

          this.floorPlansLoaded.emit(floorPlanResources);
          this.isLoading = false;
        }),
        take(1),
      )
      .subscribe();
    this.subscriptions.add(floorPlansSub);

    setTimeout(() => {
      this.changeDate();
    });
  }

  deleteParty(party: FloorPlanParty): void {
    this.partyDelete.emit({
      cardType: party.cardType,
      party: this.cardToParty(party),
      callback: (): void => {
        this.showTableInfoCards = false;
        this.selectedResourceId = undefined;
        this.selectedPartyId = undefined;
      },
    });
  }

  private getPartyType(party: Party): FloorPlanPartyType {
    if (party.seated_time) {
      return FloorPlanPartyType.serving;
    }

    if (party.checkin_time && party.type === PartyTypes.reservation) {
      return FloorPlanPartyType.checkedIn;
    }

    if (
      (party.checkin_time && party.type === PartyTypes.walkIn) ||
      party.source === PartySource.square
    ) {
      return FloorPlanPartyType.waiting;
    }

    if (party?.quoted_time < moment.now()) {
      return FloorPlanPartyType.overdue;
    }

    return FloorPlanPartyType.booked;
  }

  private cardTypeToTabType(cardType: FloorPlanPartyType): FloorPlanPartyTab {
    const tabType = FloorPlanPartyTab.booked;

    if (cardType === FloorPlanPartyType.serving || cardType === FloorPlanPartyType.waiting) {
      return cardType as unknown as FloorPlanPartyTab;
    }

    return tabType;
  }

  private setPartyCardType(party: Party): FloorPlanParty {
    const cardType = this.getPartyType(party);
    const tabType = this.cardTypeToTabType(cardType);
    const callback = (): void => {
      this.changeDate();
    };

    const callbackWithCloseTableInfo = (): void => {
      callback();
      this.closePartyOrTableInfo();
    };

    const actions: FloorPlanPartyAction[] = [
      {
        title: 'Edit info',
        buttonStyle: 'info',
        icon: AppIcon.edit,
        action: (item: FloorPlanParty): void => {
          this.partyEdit.emit({
            cardType: item.cardType,
            party: this.cardToParty(item),
            callback,
          });
        },
      },
    ];

    if (cardType === FloorPlanPartyType.serving) {
      actions.push({
        title: 'Complete',
        buttonStyle: 'success',
        action: (item: FloorPlanParty): void => {
          this.partyComplete.emit({
            cardType: item.cardType,
            party: this.cardToParty(item),
            callback: callbackWithCloseTableInfo,
          });
        },
      });
    }

    if (cardType === FloorPlanPartyType.checkedIn) {
      actions.push({
        title: 'Serve',
        buttonStyle: 'success',
        action: (item: FloorPlanParty): void => {
          this.partyServed.emit({
            cardType: item.cardType,
            party: this.cardToParty(item),
            callback,
          });
        },
      });
    }

    if (cardType === FloorPlanPartyType.waiting) {
      actions.push({
        title: 'Serve',
        buttonStyle: 'success',
        action: (item: FloorPlanParty): void => {
          this.partyServed.emit({
            cardType: item.cardType,
            party: this.cardToParty(item),
            callback: callbackWithCloseTableInfo,
          });
        },
      });
    }

    if (cardType === FloorPlanPartyType.overdue) {
      actions.push({
        title: 'Check-in',
        buttonStyle: 'success',
        action: (item: FloorPlanParty): void => {
          this.partyCheckIn.emit({
            cardType: item.cardType,
            party: this.cardToParty(item),
            callback,
          });
        },
      });
    }

    if (cardType === FloorPlanPartyType.booked) {
      actions.push({
        title: 'Check-in',
        buttonStyle: 'success',
        action: (item: FloorPlanParty): void => {
          this.partyCheckIn.emit({
            cardType: item.cardType,
            party: this.cardToParty(item),
            callback,
          });
        },
      });
    }

    actions.push({
      title: '',
      buttonStyle: 'info',
      icon: AppIcon.send,
      color: AppIconColor.white,
      isDisabled: (item) => {
        return this.messageButtonService.disableMessageButton(item);
      },
      disabledTooltip: !party.phone ? 'No phone provided' : 'Not available on Free Plan',
      tooltip: 'Text',
      buttonWidth: 36,
      action: (item: FloorPlanParty): void => {
        this.messageButtonService.sendReadySms(item, this.smsNextMessage);
      },
    });

    actions.push({
      title: '',
      buttonStyle: 'info-outline',
      icon: AppIcon.chat,
      color: AppIconColor.blue,
      tooltip: 'Chat',
      isDisabled: () => !party.phone || !this.isOnBusinessPlan,
      disabledTooltip: !party.phone ? 'No phone provided' : 'Not available on Free Plan',
      buttonWidth: 36,
      action: (item: FloorPlanParty): void => {
        this.partyChat.emit({
          cardType: item.cardType,
          party: this.cardToParty(item),
          callback,
        });
      },
    });

    return {
      ...party,
      cardType,
      tabType,
      actions,
      resourceName: party.resources
        .filter(resourceId => this.checkIfResourceEnabled(resourceId))
        .reduce((accumulator, currentResourceId, index, array) => {
          const nameWithPostfix = this.service.getResourceNameByIdWithFloorPlan(currentResourceId, this.selectedFloorPlanId);

          return index < array.length - 1 ? `${accumulator}${nameWithPostfix}, ` : `${accumulator}${nameWithPostfix}`;
        }, ''),
    };
  }

  private checkLayout(): void {
    this.isTabletLayout = this.breakpointObserver.isMatched(FLOOR_PLAN_VIEW_TABLET_BREAKPOINT);

    if (!this.isTabletLayout) {
      this.setSidebarOpen(true);
    }
  }

  private checkIfResourceEnabled(resourceId: string): boolean {
    const resource = this.resourcesService.getById(resourceId);
    if (!resource) {
      return false;
    }

    return resource.enabled;
  }

  private setTabsData(counts: Partial<Record<FloorPlanPartyTab, number>>): void {
    const tabs: SharedTabItem[] = [];
    Object.entries(counts).forEach(([title, partiesCount]) => {
      tabs.push({
        title,
        count: partiesCount,
        countColor: this.cardTypeColorMap[title as FloorPlanPartyType],
      });
    });

    if (this.tabs.length === 0 && tabs.length > this.tabIndex) {
      this.tabChange.emit(tabs[this.tabIndex]);
    }

    this.tabs = tabs;
  }

  private getPartiesByWorkTime(workTime: number[][]): Observable<Party[]> {
    return this.authRestaurantService.selectWithAccountAndLocation()
      .pipe(
        switchMap(() => this.partiesService.getPartiesByWorkTime({
          workTime,
          served: null,
          type: null,
        })),
        take(1),
        filter(() => this.isOnBusinessPlan),
        tap((parties) => {
          this.parties = parties.map(party => this.setPartyCardType(party))
            .filter((party) => {
              if (party.type === PartyTypes.togo) {
                return false;
              }

              if (this.currentFloorPlanPartiesOnly && party.resources?.length > 0) {
                let anyResourceFromThisFloor = false;

                party.resources.forEach((resourceId) => {
                  if (anyResourceFromThisFloor) {
                    return;
                  }

                  const data = this.service.resourceIdToTableDataMap[resourceId];
                  if (data?.floorPlanId === this.selectedFloorPlanId) {
                    anyResourceFromThisFloor = true;
                  }
                });

                if (!anyResourceFromThisFloor) {
                  return false;
                }
              }

              const today = moment().endOf('day')
                .valueOf();
              const selectedDateEndOfDay = moment(this.selectedDate).endOf('day')
                .valueOf();

              if (today !== selectedDateEndOfDay) {
                return true;
              }

              return moment(party.quoted_time)
                .add(this.autoRemoveTime, 'hours')
                .add(party.duration / ONE_MINUTE, 'minutes')
                .isAfter(today === selectedDateEndOfDay ? moment.now() : selectedDateEndOfDay);
            });

          this.colorTablesByClosestParties(this.parties);
          this.searchParties(this.searchTerm, this.selectedResourceId);
        }),
      );
  }

  protected readonly FloorPlanPartyTab = FloorPlanPartyTab;
}
